// Admin session store — sessionStorage-backed (dev/test only).
//
// Per the operator's brief, the admin session is persisted to
// `sessionStorage` (NOT `localStorage`) so the operator can refresh
// or navigate between SPA routes without losing their session,
// while still ensuring the session evaporates when the browser tab
// closes. The token is never exposed by the metadata API — the
// debug panel reads only the address/subject/timestamps, never
// the bearer token itself.
//
// Hard rules:
//   * The bearer token is reachable only via `getSessionToken()`,
//     which is consumed by `src/api/client.ts` to set the
//     `Authorization` header — and by no UI component. Components
//     read `useSessionMetadata()` instead.
//   * `localStorage` is NEVER touched. Storing here would survive
//     a browser-close which exceeds the dev/test threat model and
//     leaks the bearer into a long-lived profile sync.
//   * On any 401 from a request that carried a bearer header, the
//     api client calls `markSessionExpired()` — this clears the
//     session AND sets a one-shot expired flag the AuthRequired
//     screen reads to surface a clear "session expired or invalid"
//     notice rather than just bouncing the user back to /login.
//   * A storage-key prefix (`gao-admin-web:session`) avoids
//     collisions with any other SPA hosted on the same origin.

import { normalizeAuthority } from "./session-claims";

const STORAGE_KEY = "gao-admin-web:session";

/** Persisted session shape. The token IS persisted (operator brief
 *  §1-2) but it is NEVER returned by `getSessionMetadata()` and
 *  NEVER rendered by any UI component. */
export interface StoredSession {
  sessionToken: string;
  walletAddress: string;
  ownerSubject: string;
  savedAt: number;
  expiresAt: number | null;
  /**
   * The authority the SERVER minted this session under (GAOSAFE AUTH PHASE 1).
   *
   * Cached here only so the header can render without a round trip. It is never
   * the source of truth: `/v2/admin/bff/session` re-reads it from the row on
   * every check, and the server re-proves live vault membership independently.
   * A value edited in sessionStorage changes what this browser DRAWS and nothing
   * about what it may do.
   */
  authority?: SessionAuthority | null;
}

/** Server-minted authority claims. Presentation only on this side. */
export interface SessionAuthority {
  signerAddress: string | null;
  authorityVault: string | null;
  authorityChainId: number | null;
  membership: string | null;
  authVersion: number | null;
  /** The device row the SERVER bound this session to. Not the browser's own
   *  fingerprint — that is an input to the mint, not a statement about it. */
  deviceId: string | null;
}

/** Public-safe projection of the session — what UI components are
 *  allowed to see. The token is intentionally absent. */
export interface SessionMetadata {
  walletAddress: string;
  ownerSubject: string;
  savedAt: number;
  expiresAt: number | null;
  authority?: SessionAuthority | null;
}

// ─── Module-scope cache (mirrors sessionStorage) ─────────────────

let cached: StoredSession | null = null;
let expiredFlag = false;

// Diagnostic state — surfaced via the "Copy safe auth debug" button
// on Login.tsx and AuthRequired.tsx so the operator can ship a
// support-bundle without including the bearer token. None of these
// fields contain the token / signature / HMAC.
let lastFourOhOneRoute: string | null = null;
let sessionClearReason: string | null = null;

type MetadataListener = (metadata: SessionMetadata | null) => void;
const listeners = new Set<MetadataListener>();

// Keys used to persist the last-entered ownerSubject across visits
// to /login so the operator does not have to re-type it after
// bouncing back from a protected route. Kept under the same
// `gao-admin-web:` prefix so the operator can spot all SPA-owned
// rows in a single sessionStorage inspection.
const LAST_OWNER_SUBJECT_KEY = "gao-admin-web:lastOwnerSubject";

function notify(): void {
  const md = toMetadata(cached);
  for (const l of listeners) {
    try {
      l(md);
    } catch {
      // Listeners must not throw; defensive swallow.
    }
  }
}

function toMetadata(s: StoredSession | null): SessionMetadata | null {
  if (s === null) return null;
  return {
    walletAddress: s.walletAddress,
    ownerSubject: s.ownerSubject,
    savedAt: s.savedAt,
    expiresAt: s.expiresAt,
    authority: s.authority ?? null,
  };
}

// ─── Storage adapter ─────────────────────────────────────────────

/** Test-overridable storage hook. Real code uses
 *  `window.sessionStorage`; tests can inject a `Map`-backed shim.
 *  Defensive: `window` may be undefined in SSR / Node test environments. */
type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function getStorage(): StorageLike | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    // SecurityError can be raised in sandboxed iframes etc.
    return null;
  }
}

function readFromStorage(): StoredSession | null {
  const store = getStorage();
  if (store === null) return null;
  let raw: string | null;
  try {
    raw = store.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null || raw === "") return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (
      typeof parsed.sessionToken !== "string" ||
      parsed.sessionToken === "" ||
      typeof parsed.walletAddress !== "string" ||
      typeof parsed.ownerSubject !== "string" ||
      typeof parsed.savedAt !== "number"
    ) {
      return null;
    }
    return {
      sessionToken: parsed.sessionToken,
      walletAddress: parsed.walletAddress,
      ownerSubject: parsed.ownerSubject,
      savedAt: parsed.savedAt,
      expiresAt: typeof parsed.expiresAt === "number" ? parsed.expiresAt : null,
      // Re-validated rather than cast. See `normalizeAuthority`: this value
      // came back out of a store the browser's owner can edit, and although it
      // authorises nothing, it should not be able to put a malformed claim on
      // screen either.
      authority: normalizeAuthority(parsed.authority),
    };
  } catch {
    return null;
  }
}

function writeToStorage(s: StoredSession): void {
  const store = getStorage();
  if (store === null) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // Quota exceeded etc. — swallow; in-memory cache still works
    // for the current tab session.
  }
}

function removeFromStorage(): void {
  const store = getStorage();
  if (store === null) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // Defensive.
  }
}

// ─── Boot: load from sessionStorage on module init ───────────────

cached = readFromStorage();

// ─── Public API ──────────────────────────────────────────────────

/** Reach the raw bearer token. Consumed ONLY by `src/api/client.ts`
 *  to set the `Authorization` header. UI components MUST NOT call
 *  this — they read `getSessionMetadata()` / `subscribeSessionMetadata()`. */
export function getSessionToken(): string | null {
  return cached === null ? null : cached.sessionToken;
}

/** Public-safe projection: the token is absent. */
export function getSessionMetadata(): SessionMetadata | null {
  return toMetadata(cached);
}

/** Persist a fresh session. Called by the Login screen on verify
 *  success. */
export function setSession(s: StoredSession): void {
  cached = s;
  expiredFlag = false;
  sessionClearReason = null;
  writeToStorage(s);
  notify();
}

/** Clear the session — used by logout. */
export function clearSession(): void {
  cached = null;
  expiredFlag = false;
  sessionClearReason = "logout";
  removeFromStorage();
  notify();
}

/** Called by the API client when a request that carried a bearer
 *  header got a 401 from the BE. Clears the session AND sets a
 *  one-shot expired flag so the AuthRequired guard can surface a
 *  clear "session expired" notice. The optional `path` records
 *  which BFF route rejected the bearer — surfaced in the safe
 *  debug bundle (NEVER the bearer itself). */
export function markSessionExpired(path?: string): void {
  cached = null;
  expiredFlag = true;
  if (typeof path === "string" && path !== "") {
    lastFourOhOneRoute = path;
    sessionClearReason = `401 from ${path}`;
  } else {
    sessionClearReason = "401 (bearer-attached request rejected)";
  }
  removeFromStorage();
  notify();
}

/** Last BFF/auth route that returned 401 with a bearer attached.
 *  Read-only — used for the safe debug copy. */
export function getLast401Route(): string | null {
  return lastFourOhOneRoute;
}

/** Last reason the session was cleared — "logout", "401 from X",
 *  null. Used for the safe debug copy. */
export function getSessionClearReason(): string | null {
  return sessionClearReason;
}

/** Whether a sessionStorage row exists for the current tab. Reads
 *  the storage layer directly (not the in-memory cache) so the
 *  debug copy reflects what would persist across a refresh. */
export function hasSessionStorageRow(): boolean {
  const store = getStorage();
  if (store === null) return false;
  try {
    return store.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

// ─── Last-owner-subject persistence (separate from the session) ──
//
// Persisting the *input* across visits to /login is independent of
// the session itself: even when the operator has no active session,
// they should not have to re-type `op-cb-2026` every time the
// AuthRequired guard bounces them back from a protected route.

export function getLastOwnerSubject(): string | null {
  const store = getStorage();
  if (store === null) return null;
  try {
    const raw = store.getItem(LAST_OWNER_SUBJECT_KEY);
    if (typeof raw !== "string" || raw === "") return null;
    return raw;
  } catch {
    return null;
  }
}

export function setLastOwnerSubject(s: string): void {
  const store = getStorage();
  if (store === null) return;
  try {
    if (s === "") {
      store.removeItem(LAST_OWNER_SUBJECT_KEY);
    } else {
      store.setItem(LAST_OWNER_SUBJECT_KEY, s);
    }
  } catch {
    // Defensive — quota / SecurityError.
  }
}

/** Read + consume the expired flag. The first reader after
 *  `markSessionExpired()` gets `true`; subsequent reads get
 *  `false`. The AuthRequired component uses this to render the
 *  notice exactly once. */
export function readAndClearExpiredFlag(): boolean {
  const wasExpired = expiredFlag;
  expiredFlag = false;
  return wasExpired;
}

/** Subscribe to metadata changes. Returns an unsubscribe function.
 *  React components use this via `useEffect`. */
export function subscribeSessionMetadata(l: MetadataListener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

// ─── Back-compat shims (will be removed once callers migrate) ────

/** @deprecated Use `clearSession()`. Kept for legacy callers. */
export function clearSessionToken(): void {
  clearSession();
}

/** @deprecated Use `subscribeSessionMetadata`. Kept for legacy
 *  callers that only need to know whether a token is set. */
export function subscribeSessionToken(
  l: (token: string | null) => void,
): () => void {
  return subscribeSessionMetadata((md) => l(md === null ? null : "***"));
}

/** Test-only escape hatch: directly write the storage layer + cache
 *  without persisting metadata format. Used by Login.test.tsx and
 *  session.test.ts to seed the storage adapter under a known shape
 *  before component render. */
export const _testing = {
  STORAGE_KEY,
  LAST_OWNER_SUBJECT_KEY,
  readFromStorage,
  writeToStorage,
  removeFromStorage,
  reloadFromStorage(): void {
    cached = readFromStorage();
    notify();
  },
  resetDiag(): void {
    lastFourOhOneRoute = null;
    sessionClearReason = null;
  },
};
